import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiJson, apiSend, fetchMe, type Me } from "@/api/client";
import { segment } from "@/api/query";

/* Mirrors api/src/pbo_invoicing/auth/schemas.py (UserOut, UserCreate, UserUpdate). */

export type Director = Me;

export type DirectorCreateInput = {
  username: string;
  full_name: string;
  email: string | null;
  password: string;
};

export type DirectorUpdateInput = {
  full_name?: string;
  email?: string | null;
  is_active?: boolean;
  password?: string;
};

export const userKeys = {
  all: ["users"] as const,
  me: ["me"] as const,
};

export function listDirectors(): Promise<Director[]> {
  return apiJson<Director[]>("/users");
}

export function createDirector(input: DirectorCreateInput): Promise<Director> {
  return apiSend<Director>("/users", "POST", input);
}

/** PATCH semantics (the users API predates If-Match): only the given fields change. */
export function updateDirector(id: string, input: DirectorUpdateInput): Promise<Director> {
  return apiSend<Director>(`/users/${segment(id)}`, "PATCH", input);
}

/** Revokes every session of the signed-in director on success (HTTP 204). */
export function changeOwnPassword(currentPassword: string, newPassword: string): Promise<void> {
  return apiSend<void>("/me/password", "POST", {
    current_password: currentPassword,
    new_password: newPassword,
  });
}

export function useMe() {
  return useQuery({ queryKey: userKeys.me, queryFn: fetchMe });
}

export function useDirectors() {
  return useQuery({ queryKey: userKeys.all, queryFn: listDirectors });
}

export function useCreateDirector() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createDirector,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useUpdateDirector() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: DirectorUpdateInput }) =>
      updateDirector(id, input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: userKeys.all }),
        queryClient.invalidateQueries({ queryKey: userKeys.me }),
      ]),
  });
}

export function useChangeOwnPassword() {
  return useMutation({
    mutationFn: ({ current, next }: { current: string; next: string }) =>
      changeOwnPassword(current, next),
  });
}
